import { useState, useRef, useCallback, useEffect } from "react";
import { wsURL } from "../api/api";
import { useAuth } from "../features/auth/AuthContext";

// Helper function to identify the scenario based on payload
const getScenarioFromPayload = (payload) => {
  if (!payload) return "Unknown";

  // Check for overall performance scenario
  if (
    payload.teacher_id &&
    payload.selectedCourseData &&
    payload.selectedStudentData &&
    payload.avg_score
  ) {
    return "Overall Performance";
  }

  // Check for event overview scenario
  if (
    payload.teacher_id &&
    payload.selectedCourseData &&
    payload.selectedStudentData &&
    payload.avg_score &&
    payload.periodTab
  ) {
    return "Event Overview";
  }

  // Check for single event scenario (dot click)
  if (
    payload.teacher_id &&
    payload.selectedCourseData &&
    payload.selectedStudentData &&
    payload.avg_score &&
    payload.daily_date
  ) {
    return "Single Event (Dot Click)";
  }

  // Check for course-specific scenario
  if (
    payload.teacher_id &&
    payload.selectedCourseData &&
    payload.selectedStudentData &&
    payload.avg_score &&
    payload.selectedCourseData.length === 1
  ) {
    return "Single Course Performance";
  }

  // Check for student scenarios
  if (payload.student_id && !payload.teacher_id) {
    if (payload.daily_date) {
      return "Student Single Event";
    } else if (
      payload.selectedCourseData &&
      payload.selectedCourseData.length === 1
    ) {
      return "Student Single Course";
    } else {
      return "Student Overall Performance";
    }
  }

  return "Unknown Scenario";
};

const useWebSocketFeedback = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [combinedMessage, setCombinedMessage] = useState([]);
  const isIntentionalCloseRef = useRef(false);
  const connectionInProgressRef = useRef(false); // Add this to track connection in progress
  const socketRef = useRef(null); // Add this to store socket reference
  const {token} = useAuth()  

  // Store callbacks in refs to avoid stale closures
  const callbacksRef = useRef({
    cleanupMessage: (message) => {
      // Default cleanup function
      const cleaned = message
        .replace(/<STARTOFTURN>/g, "")
        .replace(/<ENDOFTURN>/g, "");
      return cleaned;              
    },
  });

  const connectWebSocket = useCallback(
    ({
      url,
      payload,
      onDataReceived,
      onComplete,
      shouldConnect = () => true,
      cleanupMessage = (message) => {
        // More conservative cleanup - only remove control tags
        const cleaned = message
          .replace(/<STARTOFTURN>/g, "")
          .replace(/<ENDOFTURN>/g, "");
        return cleaned;
      },
    }) => {
      // Log the connection attempt with scenario identification

      // Prevent multiple connections
      if (!shouldConnect() || isConnected || connectionInProgressRef.current) {
        console.log(
          "WebSocket connection skipped - already connected or in progress"
        );
        return;
      }

      // Mark connection as in progress
      connectionInProgressRef.current = true;

      // Store callbacks in ref to avoid stale closures
      callbacksRef.current = { onDataReceived, onComplete, cleanupMessage };

      setLoading(true);
      setIsConnected(true);
      setCombinedMessage([]); // Reset message array

      const socket = new WebSocket(`${wsURL}ws/students-feedback/?token=${token}`);
      socketRef.current = socket; // Store socket reference

      // Add a timeout to prevent hanging connections
      const connectionTimeout = setTimeout(() => {
        if (socket.readyState === WebSocket.CONNECTING) {
          console.warn("WebSocket connection timeout - closing connection");
          socket.close();
        }
      }, 30000); // 30 second timeout

      socket.onopen = () => {
        console.log("✅ WebSocket Connection Opened:", {
          scenario: getScenarioFromPayload(payload),
          url,
          timestamp: new Date().toISOString(),
        });
        isIntentionalCloseRef.current = false;
        connectionInProgressRef.current = false; // Reset connection in progress flag
        clearTimeout(connectionTimeout); // Clear the connection timeout

        if (payload) {
          try {
            const payloadString = JSON.stringify(payload);

            socket.send(payloadString);
          } catch (error) {
            console.error("❌ Error sending payload:", error);
          }
        }
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data && data.message) {
            const message = data.message;
            const messageType = data.type;

            // Skip connection messages
            if (
              (message === "Connected to AI Feedback Generator" ||
                message ===
                  "Connected to Student Average Score AI Feedback Generator") &&
              messageType === "connection"
            ) {
              return;
            }

            // Clear old messages when a new response starts
            if (message.includes("<STARTOFTURN>")) {
              setCombinedMessage([]);
              return; // Don't add the STARTOFTURN to the message array
            }

            // Don't add ENDOFTURN to the message array
            if (message === "<ENDOFTURN>") {
              // Process final combined message
              setCombinedMessage((prev) => {
                const finalMessage = prev.join("");
                const cleanupMessage =
                  callbacksRef.current?.cleanupMessage ||
                  ((message) => {
                    // Fallback cleanup function
                    const cleaned = message
                      .replace(/<STARTOFTURN>/g, "")
                      .replace(/<ENDOFTURN>/g, "");
                    return cleaned;
                  });
                const cleanedChatData = cleanupMessage(finalMessage);

                console.log("cleanedChatData", cleanedChatData);

                // First, call onDataReceived with the final data to ensure UI updates
                setTimeout(() => {
                  if (callbacksRef.current?.onDataReceived) {
                    callbacksRef.current.onDataReceived(
                      cleanedChatData,
                      finalMessage
                    );
                  }

                  // Then call onComplete after a small delay to ensure state updates are processed
                  setTimeout(() => {
                    if (callbacksRef.current?.onComplete) {
                      callbacksRef.current.onComplete(cleanedChatData);
                    }
                    // Close the socket after onComplete is called
                    isIntentionalCloseRef.current = true;
                    socket.close();
                  }, 100); // Small delay to ensure state updates are processed
                }, 0);

                return prev;
              });

              return;
            }

            // Add message to array and process
            setCombinedMessage((prev) => {
              const newMessages = [...prev, message];
              const rawChatData = newMessages.join("");
              const cleanupMessage =
                callbacksRef.current?.cleanupMessage ||
                ((message) => {
                  // Fallback cleanup function
                  const cleaned = message
                    .replace(/<STARTOFTURN>/g, "")
                    .replace(/<ENDOFTURN>/g, "");
                  return cleaned;
                });
              const cleanedChatData = cleanupMessage(rawChatData);

              // Use setTimeout to ensure the callback runs in the next tick
              // This helps avoid issues with React state batching
              setTimeout(() => {
                if (callbacksRef.current?.onDataReceived) {
                  callbacksRef.current.onDataReceived(
                    cleanedChatData,
                    rawChatData
                  );
                }
              }, 0);

              // Stop loader when actual content is received
              if (cleanedChatData.trim().length > 0) {
                setLoading(false);
              }

              return newMessages;
            });
          }
        } catch (error) {
          console.error("Error processing WebSocket message:", error);
          setLoading(false);
          setIsConnected(false);
          connectionInProgressRef.current = false; // Reset on error
        }
      };

      socket.onclose = (event) => {
        clearTimeout(connectionTimeout); // Clear the connection timeout
        console.log("🔌 WebSocket Connection Closed:", {
          scenario: getScenarioFromPayload(payload),
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          isIntentionalClose: isIntentionalCloseRef.current,
          timestamp: new Date().toISOString(),
        });

        // Only reset states if it wasn't an intentional close
        if (!isIntentionalCloseRef.current) {
          console.warn(
            "WebSocket closed unexpectedly - this might indicate a server issue or timeout"
          );
        }

        setLoading(false);
        setIsConnected(false);
        connectionInProgressRef.current = false; // Reset connection in progress flag
        socketRef.current = null; // Clear socket reference
      };

      socket.onerror = (error) => {
        console.error("WebSocket error:", error);
        setLoading(false);
        setIsConnected(false);
        connectionInProgressRef.current = false; // Reset on error
        socketRef.current = null; // Clear socket reference
        socket.close();
      };

      return socket;
    },
    [isConnected]
  );

  const resetState = useCallback(() => {
    setLoading(false);
    setIsConnected(false);
    setCombinedMessage([]);
    isIntentionalCloseRef.current = false;
    connectionInProgressRef.current = false; // Reset connection in progress flag

    // Close existing socket if it exists
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }

    // Reset callbacks but keep the default cleanupMessage
    callbacksRef.current = {
      cleanupMessage: (message) => {
        // Default cleanup function
        const cleaned = message
          .replace(/<STARTOFTURN>/g, "")
          .replace(/<ENDOFTURN>/g, "");
        return cleaned;
      },
    };
  }, []);

  return {
    isConnected,
    loading,
    connectWebSocket,
    resetState,
  };
};

export default useWebSocketFeedback;
